import {useEffect, useState} from 'react'

import {fetchNetworkTableData, type NetworkTableData} from './network-table-data'
import type {NetworkGraphTableRow} from './types'

export type NetworkTableStatus = 'idle' | 'loading' | 'success' | 'error'

export interface NetworkTableState {
  status: NetworkTableStatus
  rows: NetworkGraphTableRow[]
  page: number
  pageCount: number
  goToPage: (page: number) => void
}

interface RemoteNetworkTableState {
  status: NetworkTableStatus
  data: NetworkTableData | null
}

const EMPTY_ROWS: NetworkGraphTableRow[] = []

/**
 * Normalizes supplied rows and remotely loaded rows into one state for the table to render.
 * Supplied rows are immediately successful. A remote source loads once `enabled` becomes true
 * (the table view is opened), then caches its data for the lifetime of the component so reopening
 * the dialog does not refetch the potentially large payload. Bumping `reloadToken` triggers a
 * fresh fetch, which powers the "Try again" affordance after an error.
 *
 * `fetchNetworkTableData` uses `fetchPoll`, which resolves once the graph has finished building
 * (it may poll for a while first); a cancelled flag prevents state updates after unmount rather
 * than aborting the in-flight request, since the response is a cheap, idempotent GET.
 */
export function useNetworkTableData(
  url: string | undefined,
  suppliedRows: NetworkGraphTableRow[],
  enabled: boolean,
  reloadToken: number,
): NetworkTableState {
  const [page, setPage] = useState(1)
  const [state, setState] = useState<RemoteNetworkTableState>({status: 'idle', data: null})

  useEffect(() => {
    if (!enabled || !url) {
      return
    }

    let cancelled = false

    const load = async () => {
      setState(current => ({status: 'loading', data: current.data}))
      try {
        const data = await fetchNetworkTableData(url, page)
        if (!cancelled) {
          setState({status: 'success', data})
        }
      } catch {
        if (!cancelled) {
          setState({status: 'error', data: null})
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [url, enabled, reloadToken, page])

  if (!url) {
    return {
      status: 'success',
      rows: suppliedRows,
      page: 1,
      pageCount: 1,
      goToPage: setPage,
    }
  }

  return {
    status: state.status,
    rows: state.data?.rows ?? EMPTY_ROWS,
    page,
    pageCount: state.data?.pageCount ?? 1,
    goToPage: setPage,
  }
}
